import * as React from 'react';
import { match } from 'react-router';
import { History, Location } from 'history';
import * as intl from 'react-intl-universal';
import utils from 'common/utils/tool';
import Formatter from 'common/utils/formatter';
import Loading from '../Loading';
import { Interruptible } from 'common/utils/http/interrupt';
import { NavLink as Link } from 'react-router-dom';
import { LeftOutlined, PlusOutlined } from '@ant-design/icons';
import Icon from 'common/components/IconFont';
import { type RenderEmptyHandler } from 'antd/es/config-provider';
import * as Sentry from '@sentry/react';

/**
 * 模块说明
 * -------
 * pages内的类组件必须继承该组件的默认导出类
 * 提示：
 *   1、mounted状态供异步操作后setState操作容错判断
 *   2、routes/path/pathname等均为props中路由信息的代理属性
 *   3、支持三级路由下页面之间跳转的state缓存，只需组件unmount时设置cache，construct时读取cache
 *   4、translate方法为react-intl-universal中get方法的代理，进行了简单封装处理
 *   5、列表页面获取列表后须调用scrollToTop方法
 *   6、异步请求须在最外层包裹loading函数 (需要记录加载中状态的请求)
 *   7、获取异步请求状态通过withLoading函数，非必要勿操作state中的isLoading
 *   8、withLoading函数调用参数可为函数或组件，函数可接收isLoading参数，组件将被包裹Loading组件并应用加载中状态
 *   9、组件实现了defer延迟执行方法，defer操作将按后进先出的顺序在组件卸载前执行
 *   10、页面定时更新任务通过实例化Scheduler，任务必须在组件卸载前停止，建议实例化后通过defer方法延迟执行
 */

interface Fragments {
    body: React.ReactNode;
}

interface ExtProps {
    history: History;
    location: Location;
    match: match<any>;
}

interface State {
    isLoading?: boolean;
}

const deferGroup = Symbol('@@core/deferGroup');
const loadingGroup = Symbol('@@core/loadingGroup');
const fragments = Symbol('@@core/fragments');
const iconMap: any = {
    left: <LeftOutlined key="left" />,
    plus: <PlusOutlined key="plus" />,
};
type EmptyType = 'Table' | 'List' | 'Select' | 'TreeSelect' | 'Cascader' | 'Transfer';

export default class Core<P = {}, S = {}, E = {}> extends React.Component<Partial<ExtProps> & P, State & S> {
    // 工具集
    static utils = utils;
    // 格式化
    static Formatter = Formatter;
    // 加载中组件
    static Loading = Loading;
    // 组件挂载状态
    protected mounted: boolean;
    // 组件卸载执行任务
    protected [deferGroup]: Array<Function>;
    // 组件加载中任务
    protected [loadingGroup]: number = 0;

    constructor(props: Partial<ExtProps> & P) {
        super(props);
        // 组件挂载/卸载执行方法 (设置挂载状态等)
        // 注意：componentDidMount和componentWillUnmount需绑定this
        this[deferGroup] = [];
        this[loadingGroup] = 0;
        // 页面卸载时中断所有还在飞的请求，避免结果回来时页面已不在
        // Abort the still-running requests on unmount so their results land nowhere.
        this.defer(() => {
            this.interruptController.abortAll();
        });
        const { componentDidMount, componentWillUnmount } = this;
        this.componentDidMount = () => {
            this.mounted = true;
            if (componentDidMount) componentDidMount.bind(this)();
        };
        this.componentWillUnmount = () => {
            if (componentWillUnmount) componentWillUnmount.bind(this)();
            this.mounted = false;
            for (const defer of this[deferGroup].reverse()) {
                if (defer) utils.Func.pcall(defer).catch(() => null);
            }
        };
    }

    componentDidCatch(err: Error, msg: React.ErrorInfo) {
        // This boundary swallows page render errors (React unmounts the subtree, no fallback UI),
        // so Sentry would never see them — report here and keep the existing swallow behaviour.
        // 该边界会静默吞掉页面渲染错误（React 卸载子树且无兜底 UI），Sentry 收不到，
        // 因此在此手动上报，同时保持原有的「吞掉不抛」行为不变
        if (Sentry?.captureException) {
            Sentry.withScope((scope) => {
                scope.setTag('source', 'page-core-boundary');
                scope.setContext('react', { componentStack: msg?.componentStack });
                Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
            });
        }
    }

    // 延迟执行 (组件卸载前执行)
    protected defer = (v: Function) => {
        this[deferGroup].push(v);
    };

    // 文案国际化
    protected translate = (keys: string | Array<string>, value: Object = {}) => {
        if (typeof keys === 'string') {
            return intl.get(keys, value);
        } else {
            const { currentLocale } = intl.getInitOptions();
            return keys.map((key) => intl.get(key, value)).join(currentLocale === 'en-US' ? ' ' : '');
        }
    };

    // 文案国际化 (渲染HTML)
    protected translateWithHTML = (key: string, value: Object = {}) => intl.getHTML(key, value);

    // 路由路径相关信息
    protected get routes() {
        const { history, location, match } = this.props;
        return history && location && match ? { history, location, match } : null;
    }

    // 当前组件匹配路径
    protected get path() {
        const { match } = this.props;
        return match ? match.path : null;
    }

    // 当前路由路径
    protected get pathname() {
        const { location } = this.props;
        return location ? location.pathname : null;
    }

    // 当前路由持久化状态
    protected get persist() {
        const { location } = this.props;
        return location ? location.state || {} : null;
    }

    // 当前路由参数
    protected get params() {
        const { match } = this.props;
        return match ? match.params : null;
    }

    // 页面片段
    private [fragments]: Partial<Fragments & E> = {};

    // 获取页面片段
    protected get fragments() {
        return this[fragments];
    }

    // 设置页面片段
    protected set fragments(value: Partial<Fragments & E>) {
        this[fragments] = value;
    }

    // 异步请求
    protected loading = <T extends (...args: Array<any>) => any>(func: T): T =>
        (async (...args: Array<any>) => {
            this[loadingGroup] += 1;
            this.setState({ isLoading: this[loadingGroup] > 0 });
            const { value, err } = await utils.Func.pcall(func, ...args);
            if (this.mounted) {
                this[loadingGroup] -= 1;
                this.setState({ isLoading: this[loadingGroup] > 0 });
            }
            if (err) {
                // 请求被主动打断：静默结束，不作为错误抛出（否则会变成 unhandled rejection）
                // Request aborted on purpose: end silently instead of throwing (it would otherwise
                // surface as an unhandled rejection).
                if (err?.name === 'AbortError' || err?.code === 20) return undefined;
                throw err;
            }
            return value;
        }) as T;

    // 可打断请求控制器
    private readonly interruptController = new Interruptible();

    /**
     * 发起一次「可打断」的请求：同一 key 的上一次未完成请求会被 abort
     * 典型用法：列表页在检索栏改动后发起新一轮检索，打断上一轮还在飞的请求
     * Starts an interruptible request: the previous in-flight request under the same key is aborted.
     * Typical use: a list page starts a new round of search and cancels the previous one.
     * @param key 区分用途，如 'list'；不同 key 互不打断
     */
    protected interruptible = (key: string): AbortSignal | undefined => this.interruptController.signal(key);

    /**
     * 中断指定前缀的所有「可打断」请求
     * 典型用法：列表发起新一轮检索时，把上一轮还在飞的展开请求一并失效
     * Aborts every interruptible request whose key starts with the prefix.
     * Typical use: a list starts a new round of search and invalidates the expand requests still in flight.
     * @param prefix key 前缀，如 'expand:'
     */
    protected abortInterruptible = (prefix: string): void => {
        this.interruptController.abortByPrefix(prefix);
    };

    // 异步请求锁
    protected isLoading = (): boolean => !this.mounted || this.state.isLoading;

    // 异步请求加载中组件
    protected withLoading = (children: React.ReactNode | ((isLoading: boolean) => React.ReactNode)) => {
        const { isLoading = this[loadingGroup] > 0 } = this.state || {};
        if (typeof children === 'function') return children(isLoading);
        return <Loading enabled={isLoading}>{children}</Loading>;
    };

    // 跳转链接组件
    protected withLink = (url: any, text: string | React.ReactNode, icon?: string, style = {}) => {
        if (!url || !text) return '';
        return (
            <Link to={url}>
                {[
                    !!icon && (iconMap[icon] || <Icon key="icon" className={`anticon-${icon}`} />),
                    <span key="text" style={{ marginLeft: 4, ...style }}>
                        {text}
                    </span>,
                ]}
            </Link>
        );
    };

    // 渲染react元素
    protected renderElement = (fn: any) => {
        return typeof fn === 'function' ? fn() : fn;
    };

    // 获取表单值 (包含校验)
    protected getFormValues = async (
        form: any,
        fields?: Array<string>,
    ): Promise<{
        [key: string]: any;
    }> => {
        const values = await new Promise((resolve, reject) => {
            form.validateFieldsAndScroll(fields, (err: any, values: any) => {
                if (err) return reject(err);
                return resolve(values);
            });
        });
        return Core.utils.obj(values).shake();
    };
}

export interface ConfigConsumerProps {
    getPopupContainer?: (triggerNode?: HTMLElement) => HTMLElement;
    rootPrefixCls?: string;
    getPrefixCls: (suffixCls: string, customizePrefixCls?: string) => string;
    renderEmpty: (prefix: string, render?: RenderEmptyHandler | EmptyType) => React.ReactNode;
}

export class ConfigConsumer extends React.Component<{
    children: (config: ConfigConsumerProps) => React.ReactElement<any> | Array<JSX.Element>;
}> {
    // 安全渲染空值
    renderEmpty = (prefixCls: string, render: EmptyType | Function | null) => {
        const prefix = this.getPrefixCls(prefixCls);
        return <div className={`${prefix}-empty-wrapper`}>{typeof render === 'function' ? render() : render}</div>;
    };

    // 全局样式前缀
    getPrefixCls = (suffixCls: string, customizePrefixCls?: string) => {
        if (customizePrefixCls) return customizePrefixCls;
        return `v4-${suffixCls}`;
    };

    render() {
        return this.props.children({
            getPrefixCls: this.getPrefixCls,
            renderEmpty: this.renderEmpty as RenderEmptyHandler,
        });
    }
}
